import Navbar from "../../components/common/Navbar"
import Header from "../../components/common/Header"
import Footer from "../../components/common/Footer"
import Table from "../../components/league-table/Table"
import { useState, useEffect, useMemo, FC } from "react"
import styled from "styled-components"
import useNotyf from "../../context/useNotify"
import LeagueService from "../../services/league"
import HelperService from "../../services/helper"
import { League } from "../../models/league"
import { QueryParams } from "../../models/misc"
import { LeagueStats, TableEntry } from "../../models/leagueStats"
import Dropdown from "../../components/common/Dropdown"
import leagueStatsService from "../../services/leagueStats"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTwitch } from "@fortawesome/free-brands-svg-icons"
import LoadingSpinner from "../../components/common/LoadingSpinner"
import { faBarChart, faCalendarAlt, faCalendarDay, faClock, faShieldAlt, faTable } from "@fortawesome/free-solid-svg-icons"
import { Tab, Tabs, TabList, TabPanel } from "react-tabs"
import LeagueStatsTable, { TeamData } from "../../components/team-hub/stats/LeagueStatsTable"

const Tables: FC = () => {
  const notyf = useNotyf()

  // Data
  const [leagues, setLeagues] = useState<League[]>([]);
  const [tableEntries, setTableEntries] = useState<TableEntry[]>([]);
  const [leagueStats, setLeagueStats] = useState<LeagueStats>()
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true)
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true)
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  const leagueOptions = useMemo(() => {
    return leagues.map(league => ({ 
      label: `${league.leagueName} - DIV ${league.division}`, 
      value: league 
    }));
  }, [leagues]);

  // API
  const fetchLeagues = async () => {
    try {
      const filters: QueryParams = { 
        page: 1,
        pageSize: 100,
      };
      const data = await LeagueService.listLeagues(filters)
      setLeagues(data.data)
      return data.data
    } catch (err) {
      console.log("Error fetching leagues: ", err)
      HelperService.showNotfyErr(notyf, err, "Error fetching leagues.")
    }
  }

  const fetchLeagueStats = async (leagueId: string) => {
    try {
      setIsTableLoading(true)
      const data = await leagueStatsService.getLeagueStats(leagueId)
      setLeagueStats(data)
      setIsPageLoading(false)
    } catch (err) {
      HelperService.showNotfyErr(notyf, err, "Error fetching league stats.")
    } finally {
      setIsTableLoading(false)
    }
  }

  // Methods
  const handleSelectLeague = (selectedOption: any) => {
    if (!selectedOption) {
        return
    }
    const selectedLeagueId = selectedOption.value._id;
    fetchLeagueStats(selectedLeagueId)
    setActiveTabIndex(0)
  };

  const findLeagueTeams = ():TeamData[] => {
    if (!leagueStats) {
      return []
    }
    return leagueStats.table.map((team) => {
      return {
        name: team.name,
        _id: team.teamId,
      } as TeamData
    });
  }

  // Hooks
  useEffect(() => {
    if (leagueStats) {
      setTableEntries(leagueStats.table);
    }
  }, [leagueStats]);

  useEffect(() => {
    const init = async () => {
      const data = await fetchLeagues()
      if (data?.length) {
        fetchLeagueStats(data[0]._id)
      }
    }
    init()
  }, []);

  return (
    <>
      {/* Navbar */}
      <Navbar />
      {/* Page header */}
      <Header title={"League Tables"} />
      {/* League options */}
      {isPageLoading ? (
        <div className='d-flex justify-content-center align-items-center py-5'>
          <LoadingSpinner />
        </div>
      ) : (
        <Container className="container">
          <Controller className="gradient-gray">
          <Row className="gradient-gray-light">
            <div className="row w-100">
              <div className="col-md-6 p-0 pe-md-2">
                <div className="mb-3">
                  <Dropdown
                    options={leagueOptions}
                    onSelect={handleSelectLeague}
                    text="Select League"
                  />
                </div>
              </div>
              <div className="col-md-6 p-0 ps-md-2">
                <div className="mb-3 d-flex justify-content-end">
                  <LiveLinkButton href={'https://www.twitch.tv/vgcesportstv'} target="_blank" rel="noopener noreferrer" className="btn">
                    <FontAwesomeIcon icon={faTwitch} />
                    <span className="">VGC TV</span>
                  </LiveLinkButton>
                </div>
              </div>
            </div>
            {leagueStats && (
              <div className="row">
                <div className="col-md-6 mb-2">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                    <span>{`${leagueStats.leagueName} - DIV ${leagueStats.division}`}</span>
                  </div>
                </div>
                <div className="col-md-6 mb-2">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                    <span>{leagueStats.startDate}</span>
                  </div>
                </div>
                <div className="col-md-6 mb-2">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faCalendarDay} className="me-2" />
                    <span>{leagueStats.dates.join(", ")}</span>
                  </div>
                </div>
                <div className="col-md-6 mb-2">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faClock} className="me-2" />
                    <span>{leagueStats.times.join(", ")}</span>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faTwitch} className="me-2" />
                    <span>Don't miss the live streams!</span>
                  </div>
                </div>
              </div>
            )}
          </Row>
          </Controller>
            {/* Tabs */}
            <StyledTabs selectedIndex={activeTabIndex} onSelect={(index) => setActiveTabIndex(index)}>
              <TabList>
                <>
                  <Tab>
                    <FontAwesomeIcon icon={faTable} /> Table
                  </Tab>
                  <Tab>
                    <FontAwesomeIcon icon={faBarChart} /> Stats
                  </Tab>
                </>
              </TabList>
            <TabPanel>
              {/* league table */}
              {isTableLoading ? (
                <div className='d-flex justify-content-center align-items-center py-5'>
                  <LoadingSpinner />
                </div>
              ) : null}
              {!isTableLoading && tableEntries && leagueStats ? (
                <TableContainer className='gradient-gray'>
                  <Table teams={tableEntries} division={leagueStats.division} showLiveLinks={true} />
                </TableContainer>
              ) : null}
            </TabPanel>
            <TabPanel>
              {/* league stats */}
              {leagueStats ? (
                <TableContainer className='gradient-gray'>
                  <StatsContainer className="px-2 py-2">
                    <LeagueStatsTable leagueId={leagueStats.leagueId} teams={findLeagueTeams()} />
                  </StatsContainer>
                </TableContainer>
              ) : (
                <div className='d-flex justify-content-center align-items-center py-5'>
                  <LoadingSpinner />
                </div>
              )}
            </TabPanel>
          </StyledTabs>
        </Container>
      )}
      <Footer />
    </>
  )
}

export default Tables

const StyledTabs = styled(Tabs)`
  .react-tabs__tab-list {
    border-bottom: 1px solid #5fa8e6;
    display: flex;
    justify-content: space-around;
    background: linear-gradient(120deg, #1a1c28 60%, #0f1219 100%);
    border-radius: 8px;
    margin-top: 0.5rem;
    border: 2px solid #5fa8e6;
    box-shadow: 0 0 4px 0.5px #00eaff;
  }

  .react-tabs__tab {
    padding: 12px 15px;
    cursor: pointer;
    color: #fff;
    transition: all 0.3s ease;
    border-radius: 6px;
    margin: 0 4px;

    &:hover {
      background: linear-gradient(120deg, #00eeffbd 60%, #232632 100%);
      box-shadow: inset 0 0 8px rgba(0, 234, 255, 0.2);
      color: #000000ff;
    }

    &--selected {
      background: linear-gradient(120deg, #1a1c28 60%, #0f1219 100%);
      color: #fff;
      border: 1px solid #5fa8e6;
      box-shadow: 0 0 6px 1px #00eaff;
    }
  }

  .react-tabs__tab-panel {
    margin-top: 0;
  }
`;

const Container = styled.div`
  max-width: 600px;
  min-height: 90vh;
`

const Controller = styled.div`
  border-radius: 8px;
  background: linear-gradient(120deg, #23263a 60%, #1a1c28 100%);
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  border: 2px solid #5fa8e6;
  box-shadow: 0 0 4px 0.5px #00eaff;
`

const Row = styled.div`
  width: 100%;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  background: linear-gradient(120deg, #23263a 60%, #1a1c28 100%);
  border-radius: 8px;
  margin: 0;
  border: 2px solid #5fa8e6;
  padding: 1rem;
  box-shadow: 0 0 4px 0.5px #00eaff;
  @media (min-width: 991px) {
    min-height: 100px;
  }
`

const TableContainer = styled.div`
  width: 100%;
  margin-top: 1rem;
  border-radius: 8px;
`

const LiveLinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background-color: #6441a5;
  color: #fff;
  border-radius: 4px;
  margin: 0;
  text-decoration: none;
  transition: background-color 0.3s ease;
  height: 100%;
  width: 100%;

  &:hover {
    background-color: #4e337f;
    text-decoration: none;
    color: #fff;
  }
`;

const StatsContainer = styled.div`  
  width: 100%;
  background-size: cover;
  @media (max-width: 1200px) {
    font-size: small;
  }
  max-width: 870px;
  border: 2px solid #5fa8e6;
  border-radius: 8px;
  background: linear-gradient(120deg, #000000ff 60%, #040a2cff 100%);
  box-shadow: 0 0 4px 0.5px #00eaff;
`
